import {
  useMutation,
  UseMutationOptions,
  UseMutationResult,
} from "react-query";
import { useRef } from "react";

/**
 * A custom hook that wraps react-query's useMutation to ensure a minimum
 * display time for loading indicators, preventing jarring UI flashes.
 *
 * @param mutationFn The async function to execute.
 * @param options The standard react-query useMutation options.
 * @param minDuration The minimum time in milliseconds to show the loading state (e.g., 2000 for 2s).
 * @returns The standard react-query useMutation result.
 */
export function useDelayedMutation<
  TData = unknown,
  TError = unknown,
  TVariables = void,
  TContext = unknown
>(
  mutationFn: (variables: TVariables) => Promise<TData>,
  options?: Omit<
    UseMutationOptions<TData, TError, TVariables, TContext>,
    "mutationFn"
  >,
  minDuration: number = 2000 // Default minimum duration of 2 seconds
): UseMutationResult<TData, TError, TVariables, TContext> {
  const startTimeRef = useRef<number | null>(null);

  const wrappedOptions: UseMutationOptions<
    TData,
    TError,
    TVariables,
    TContext
  > = {
    ...options,
    onMutate: async (variables) => {
      // Record the start time when the mutation begins
      startTimeRef.current = Date.now();
      // Call the original onMutate if it exists
      if (options?.onMutate) {
        return options.onMutate(variables);
      }
    },
    onSettled: async (data, error, variables, context) => {
      const endTime = Date.now();
      const startTime = startTimeRef.current;

      if (startTime) {
        const duration = endTime - startTime;
        const remainingTime = minDuration - duration;

        if (remainingTime > 0) {
          // If the mutation finished faster than our minimum duration,
          // wait for the remaining time.
          await new Promise((resolve) => setTimeout(resolve, remainingTime));
        }
      }

      // Now that the minimum time has passed, call the original onSettled.
      if (options?.onSettled) {
        options.onSettled(data, error, variables, context);
      }
    },
  };

  return useMutation(mutationFn, wrappedOptions);
}
